<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import StructuredField from './StructuredField.vue'
import type {
  ProviderItem,
  ProviderLoadRequest,
  RuntimeClientBundle,
  RuntimeField,
  RuntimeFieldState,
  RuntimeProvider,
} from './types'

const props = defineProps<{
  bundle: RuntimeClientBundle
  values: Record<string, unknown>
  fieldStates?: Record<string, RuntimeFieldState>
  disabled?: boolean
  submitting?: boolean
  submitLabel?: string
  providerLoader?: (providerId: string, request?: ProviderLoadRequest) => Promise<ProviderItem[]>
}>()

const emit = defineEmits<{
  'update:values': [values: Record<string, unknown>]
  submit: []
}>()

const providerOptions = ref<Record<string, ProviderItem[]>>({})
const providerErrors = ref<Record<string, string>>({})
const providerLoading = ref<Record<string, boolean>>({})
const providerSearch = ref<Record<string, string>>({})
const fieldBusy = ref<Record<string, boolean>>({})
const readingFiles = computed(() => Object.entries(fieldBusy.value).some(([id, busy]) => busy && props.bundle.fields[id] && stateFor(id).visible))
const providerGenerations: Record<string, number> = {}
const searchTimers = new Map<string, ReturnType<typeof setTimeout>>()
onBeforeUnmount(() => { for (const timer of searchTimers.values()) clearTimeout(timer); for (const id of Object.keys(providerGenerations)) providerGenerations[id]++ })
function searchOptions(providerId: string, text: string) {
  if (props.disabled) return
  providerSearch.value[providerId] = text
  clearTimeout(searchTimers.get(providerId))
  searchTimers.set(providerId, setTimeout(() => loadProvider(providerId, props.values, text), 250))
}
function groupChanged(fieldId: string, raw: string, checked: boolean) {
  const current = Array.isArray(props.values[fieldId]) ? props.values[fieldId] as unknown[] : []
  const value = coerceSelectValue(props.bundle.fields[fieldId], raw)
  updateValue(fieldId, checked ? [...current, value] : current.filter(item => String(item) !== raw))
}
const snapshotIndexes = new Map<string, { metaKey: string; items: Map<string, ProviderItem[]> }>()

const order = computed(() => {
  const configured = props.bundle.layout?.order ?? []
  return configured.length ? configured : Object.keys(props.bundle.fields)
})

function stateFor(fieldId: string) {
  const field = props.bundle.fields[fieldId]
  const state = props.fieldStates?.[fieldId]
  return {
    visible: state?.visible ?? !field.hidden,
    required: state?.required ?? Boolean(field.required),
    readonly: state?.readonly ?? Boolean(field.readonly),
  }
}

function updateValue(fieldId: string, value: unknown) {
  if (props.disabled) return
  emit('update:values', {
    ...props.values,
    [fieldId]: value,
  })
}

function inputValue(fieldId: string): string | number {
  const value = props.values[fieldId]
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return value
  return String(value)
}

function checkboxValue(fieldId: string): boolean {
  return Boolean(props.values[fieldId])
}

function selectValue(fieldId: string): string | string[] {
  const value = props.values[fieldId]
  if (Array.isArray(value)) return value.map(String)
  if (value === null || value === undefined) return ''
  return String(value)
}

function providerForField(field: RuntimeField): RuntimeProvider | undefined {
  if (!field.provider) return undefined
  return props.bundle.providers[field.provider]
}

function providerDependencies(provider: RuntimeProvider | undefined): string[] {
  if (!provider || provider.type === 'inline') return []
  return Array.isArray(provider.depends_on) ? provider.depends_on : []
}

function dependenciesReady(provider: RuntimeProvider | undefined, values = props.values): boolean {
  return providerDependencies(provider).every(fieldId => !isEmpty(values[fieldId]))
}

function selectDisabled(fieldId: string): boolean {
  const field = props.bundle.fields[fieldId]
  const provider = providerForField(field)
  return stateFor(fieldId).readonly
    || Boolean(field.provider && providerLoading.value[field.provider])
    || !dependenciesReady(provider)
}

function primitiveValueType(valueType?: string): string {
  const normalized = (valueType || 'string').toLowerCase()
  if (['number', 'float', 'decimal'].includes(normalized)) return 'number'
  if (['integer', 'int'].includes(normalized)) return 'integer'
  if (['boolean', 'bool'].includes(normalized)) return 'boolean'
  return 'string'
}

function coerceSelectValue(field: RuntimeField, raw: string): unknown {
  if (raw === '') return null
  const primitive = primitiveValueType(field.value_type)
  if (primitive === 'number') return Number(raw)
  if (primitive === 'integer') return Number.parseInt(raw, 10)
  if (primitive === 'boolean') return raw === 'true'
  return raw
}

function onSelectChange(fieldId: string, event: Event) {
  const field = props.bundle.fields[fieldId]
  const select = event.target as HTMLSelectElement
  if (field.multiple) {
    updateValue(
      fieldId,
      Array.from(select.selectedOptions).map(item => coerceSelectValue(field, item.value)),
    )
    return
  }
  updateValue(fieldId, coerceSelectValue(field, select.value))
}

async function loadProvider(providerId: string, values: Record<string, unknown> = props.values, search = '') {
  if (props.disabled) return
  const generation = (providerGenerations[providerId] || 0) + 1
  providerGenerations[providerId] = generation
  const provider = props.bundle.providers[providerId]
  if (!provider) return

  if (provider.type === 'inline') {
    providerOptions.value[providerId] = provider.items ?? []
    providerErrors.value[providerId] = ''
    return
  }

  if (!dependenciesReady(provider, values)) {
    providerOptions.value[providerId] = []
    providerErrors.value[providerId] = ''
    providerLoading.value[providerId] = false
    return
  }

  if (provider.type === 'snapshot') {
    const items = filterSnapshotItems(providerId, provider, values)
    providerOptions.value[providerId] = items
    providerErrors.value[providerId] = ''
    providerLoading.value[providerId] = false

    if (!search && provider.value_policy === 'keep_if_valid') {
      clearInvalidTargets(providerId, items, values)
    }
    return
  }

  if (provider.type !== 'remote' || !props.providerLoader) return

  try {
    providerLoading.value[providerId] = true
    providerErrors.value[providerId] = ''
    const items = await props.providerLoader(providerId, { values, search })
    if (props.disabled || providerGenerations[providerId] !== generation) return
    const selected = new Set(Object.entries(props.bundle.fields).filter(([, field]) => field.provider === providerId).flatMap(([id]) => Array.isArray(values[id]) ? (values[id] as unknown[]).map(String) : [String(values[id] ?? '')]))
    const retained = search ? (providerOptions.value[providerId] || []).filter(item => selected.has(String(item.value)) && !items.some(next => String(next.value) === String(item.value))) : []
    providerOptions.value[providerId] = [...retained, ...items]

    if (!search && provider.value_policy === 'keep_if_valid') {
      clearInvalidTargets(providerId, items, values)
    }
  } catch (error) {
    if (props.disabled || providerGenerations[providerId] !== generation) return
    providerErrors.value[providerId] = error instanceof Error
      ? error.message
      : 'Не удалось загрузить значения'
  } finally {
    if (providerGenerations[providerId] === generation) providerLoading.value[providerId] = false
  }
}

function filterSnapshotItems(
  providerId: string,
  provider: Extract<RuntimeProvider, { type: 'snapshot' }>,
  values: Record<string, unknown>,
): ProviderItem[] {
  const filters = provider.filters ?? []
  if (!filters.length) return provider.items ?? []

  // The common parent -> child case has one snapshot filter. Build a small
  // in-memory index once so changing the parent is O(1) instead of scanning
  // the complete snapshot on every interaction. Multi-filter providers fall
  // back to the generic matcher.
  if (filters.length === 1) {
    const filter = filters[0]
    const expected = values[filter.source_field]
    if (isEmpty(expected)) return []

    let cached = snapshotIndexes.get(providerId)
    if (!cached || cached.metaKey !== filter.meta_key) {
      const items = new Map<string, ProviderItem[]>()
      for (const item of provider.items ?? []) {
        for (const comparable of comparableValues(item.meta?.[filter.meta_key])) {
          const bucket = items.get(comparable) ?? []
          bucket.push(item)
          items.set(comparable, bucket)
        }
      }
      cached = { metaKey: filter.meta_key, items }
      snapshotIndexes.set(providerId, cached)
    }

    const expectedValues = Array.isArray(expected) ? expected : [expected]
    const result: ProviderItem[] = []
    const seen = new Set<string>()
    for (const expectedValue of expectedValues) {
      for (const item of cached.items.get(String(expectedValue)) ?? []) {
        const key = String(item.value)
        if (seen.has(key)) continue
        seen.add(key)
        result.push(item)
      }
    }
    return result
  }

  return (provider.items ?? []).filter(item => {
    return filters.every(filter => {
      const expected = values[filter.source_field]
      if (isEmpty(expected)) return false
      const actual = item.meta?.[filter.meta_key]
      const actualValues = comparableValues(actual)
      const expectedValues = Array.isArray(expected) ? expected : [expected]
      return expectedValues.some(expectedValue => actualValues.includes(String(expectedValue)))
    })
  })
}

function comparableValues(value: unknown): string[] {
  const result: string[] = []

  function visit(current: unknown) {
    if (current === null || current === undefined || current === '') return

    if (Array.isArray(current)) {
      current.forEach(visit)
      return
    }

    if (typeof current === 'object') {
      // Bitrix property payloads often use numeric keys as property-value row
      // identifiers. They are not linked element IDs; only nested values are.
      Object.values(current as Record<string, unknown>).forEach(visit)
      return
    }

    result.push(String(current))
  }

  visit(value)
  return result
}

async function loadProviders(values: Record<string, unknown> = props.values) {
  for (const [providerId] of Object.entries(props.bundle.providers)) {
    await loadProvider(providerId, values)
  }
}

function clearInvalidTargets(
  providerId: string,
  items: ProviderItem[],
  values: Record<string, unknown>,
) {
  const provider = props.bundle.providers[providerId]
  if (!provider || provider.type === 'inline') return
  const valid = new Set(items.map(item => String(item.value)))
  let next: Record<string, unknown> | null = null

  for (const fieldId of provider.target_fields ?? []) {
    const field = props.bundle.fields[fieldId]
    const current = values[fieldId]
    if (isEmpty(current)) continue

    const currentValues = Array.isArray(current) ? current : [current]
    const isValid = currentValues.every(value => valid.has(String(value)))
    if (isValid) continue

    next ??= { ...values }
    next[fieldId] = field?.multiple ? [] : null
  }

  if (next) emit('update:values', next)
}

function clearProviderTargets(
  providerId: string,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const provider = props.bundle.providers[providerId]
  if (!provider || provider.type === 'inline') return values

  const next = { ...values }
  for (const fieldId of provider.target_fields ?? []) {
    const field = props.bundle.fields[fieldId]
    next[fieldId] = field?.multiple ? [] : null
  }
  return next
}

function isEmpty(value: unknown): boolean {
  return value === null
    || value === undefined
    || value === ''
    || (Array.isArray(value) && value.length === 0)
}

function changedDependencyFields(
  current: Record<string, unknown>,
  previous: Record<string, unknown>,
): Set<string> {
  const watched = new Set<string>()
  for (const provider of Object.values(props.bundle.providers)) {
    for (const fieldId of providerDependencies(provider)) watched.add(fieldId)
  }

  const changed = new Set<string>()
  for (const fieldId of watched) {
    if (JSON.stringify(current[fieldId]) !== JSON.stringify(previous[fieldId])) {
      changed.add(fieldId)
    }
  }
  return changed
}

watch(
  () => props.values,
  async (current, previous) => {
    if (props.disabled || !previous) return
    const changed = changedDependencyFields(current, previous)
    if (!changed.size) return

    const affectedProviders = Object.entries(props.bundle.providers)
      .filter(([, provider]) => providerDependencies(provider).some(fieldId => changed.has(fieldId)))
      .map(([providerId]) => providerId)

    let effectiveValues = current
    let cleared = false

    for (const providerId of affectedProviders) {
      const provider = props.bundle.providers[providerId]
      if (provider && provider.type !== 'inline' && (provider.value_policy ?? 'clear') === 'clear') {
        effectiveValues = clearProviderTargets(providerId, effectiveValues)
        cleared = true
      }
    }

    if (cleared) emit('update:values', effectiveValues)

    for (const providerId of affectedProviders) {
      clearTimeout(searchTimers.get(providerId))
      providerSearch.value[providerId] = ''
      await loadProvider(providerId, effectiveValues)
    }
  },
  { deep: true },
)

watch(
  () => `${props.bundle.form_id}:${props.bundle.revision}`,
  () => {
    snapshotIndexes.clear()
    void loadProviders()
  },
)

watch(() => props.disabled, disabled => {
  if (disabled) {
    for (const timer of searchTimers.values()) clearTimeout(timer)
    searchTimers.clear()
    for (const id of Object.keys(providerGenerations)) { providerGenerations[id]++; providerLoading.value[id] = false }
  } else void loadProviders()
})
onMounted(() => loadProviders())
</script>

<template>
  <form class="sas-form" @submit.prevent="!disabled && !readingFiles && !submitting && emit('submit')">
    <fieldset class="sas-form-fields" :disabled="disabled || submitting">
    <div
      v-for="fieldId in order"
      :key="fieldId"
      v-show="stateFor(fieldId).visible"
      class="sas-field"
    >
      <label v-if="bundle.fields[fieldId].type !== 'checkbox'" class="sas-field__label" :for="fieldId">
        {{ bundle.fields[fieldId].label }}
        <span v-if="stateFor(fieldId).required" class="sas-field__required">*</span>
      </label>

      <div v-if="bundle.fields[fieldId].description" class="sas-field__description">
        {{ bundle.fields[fieldId].description }}
      </div>

      <input v-if="bundle.fields[fieldId].provider && bundle.providers[bundle.fields[fieldId].provider!]?.type === 'remote' && (bundle.providers[bundle.fields[fieldId].provider!] as any).supports_search" type="search" class="sas-control sas-search" :aria-label="bundle.fields[fieldId].label + ': поиск'" placeholder="Поиск на портале…" :disabled="!stateFor(fieldId).visible || stateFor(fieldId).readonly" :value="providerSearch[bundle.fields[fieldId].provider!] || ''" @input="searchOptions(bundle.fields[fieldId].provider!, ($event.target as HTMLInputElement).value)">
      <input
        v-if="['text', 'email', 'phone', 'url', 'time'].includes(bundle.fields[fieldId].type)"
        :id="fieldId"
        class="sas-control"
        :type="bundle.fields[fieldId].type === 'phone' ? 'tel' : bundle.fields[fieldId].type"
        :value="inputValue(fieldId)"
        :placeholder="bundle.fields[fieldId].placeholder ?? ''"
        :minlength="bundle.fields[fieldId].min_length ?? undefined"
        :maxlength="bundle.fields[fieldId].max_length ?? undefined"
        :required="stateFor(fieldId).required"
        :readonly="stateFor(fieldId).readonly"
        :disabled="!stateFor(fieldId).visible"
        @input="updateValue(fieldId, ($event.target as HTMLInputElement).value)"
      />

      <textarea
        v-else-if="bundle.fields[fieldId].type === 'textarea'"
        :id="fieldId"
        class="sas-control sas-control--textarea"
        :value="inputValue(fieldId)"
        :placeholder="bundle.fields[fieldId].placeholder ?? ''"
        :minlength="bundle.fields[fieldId].min_length ?? undefined"
        :maxlength="bundle.fields[fieldId].max_length ?? undefined"
        :rows="bundle.fields[fieldId].rows ?? 4"
        :required="stateFor(fieldId).required"
        :readonly="stateFor(fieldId).readonly"
        :disabled="!stateFor(fieldId).visible"
        @input="updateValue(fieldId, ($event.target as HTMLTextAreaElement).value)"
      />

      <input
        v-else-if="bundle.fields[fieldId].type === 'number'"
        :id="fieldId"
        class="sas-control"
        type="number"
        :value="inputValue(fieldId)"
        :min="bundle.fields[fieldId].min ?? undefined"
        :max="bundle.fields[fieldId].max ?? undefined"
        :step="bundle.fields[fieldId].step ?? 'any'"
        :required="stateFor(fieldId).required"
        :readonly="stateFor(fieldId).readonly"
        :disabled="!stateFor(fieldId).visible"
        @input="updateValue(fieldId, ($event.target as HTMLInputElement).value === '' ? null : Number(($event.target as HTMLInputElement).value))"
      />

      <select
        v-else-if="bundle.fields[fieldId].type === 'select'"
        :id="fieldId"
        class="sas-control"
        :value="selectValue(fieldId)"
        :multiple="Boolean(bundle.fields[fieldId].multiple)"
        :required="stateFor(fieldId).required"
        :disabled="!stateFor(fieldId).visible || selectDisabled(fieldId)"
        @change="onSelectChange(fieldId, $event)"
      >
        <option v-if="!bundle.fields[fieldId].multiple" value="">
          {{ bundle.fields[fieldId].placeholder || 'Выберите значение' }}
        </option>
        <option
          v-for="item in providerOptions[bundle.fields[fieldId].provider || ''] || []"
          :key="String(item.value)"
          :value="String(item.value)"
        >
          {{ item.label }}
        </option>
      </select>

      <fieldset v-else-if="['radio', 'checkbox_group'].includes(bundle.fields[fieldId].type)" class="sas-choice-group" :id="fieldId" :aria-label="bundle.fields[fieldId].label" :disabled="!stateFor(fieldId).visible || selectDisabled(fieldId)">
        <label v-for="option in providerOptions[bundle.fields[fieldId].provider || ''] || []" :key="String(option.value)" class="sas-checkbox">
          <input :type="bundle.fields[fieldId].type === 'radio' ? 'radio' : 'checkbox'" :name="fieldId" :value="String(option.value)" :checked="bundle.fields[fieldId].type === 'radio' ? String(values[fieldId]) === String(option.value) : (Array.isArray(values[fieldId]) && (values[fieldId] as unknown[]).map(String).includes(String(option.value)))" :required="bundle.fields[fieldId].type === 'radio' && stateFor(fieldId).required" @change="bundle.fields[fieldId].type === 'radio' ? updateValue(fieldId, coerceSelectValue(bundle.fields[fieldId], String(option.value))) : groupChanged(fieldId, String(option.value), ($event.target as HTMLInputElement).checked)">
          <span>{{ option.label }}</span>
        </label>
      </fieldset>
      <StructuredField v-else-if="['money', 'file', 'multivalue', 'crm_multifield'].includes(bundle.fields[fieldId].type)" :field="bundle.fields[fieldId]" :field-id="fieldId" :value="values[fieldId]" :disabled="!stateFor(fieldId).visible || stateFor(fieldId).readonly" :required="stateFor(fieldId).required" @change="updateValue(fieldId, $event)" @busy="fieldBusy[fieldId] = $event" />
      <label
        v-else-if="bundle.fields[fieldId].type === 'checkbox'"
        class="sas-checkbox"
        :class="{ 'sas-checkbox--disabled': stateFor(fieldId).readonly }"
      >
        <input
          :id="fieldId"
          type="checkbox"
          :checked="checkboxValue(fieldId)"
          :required="stateFor(fieldId).required"
          :disabled="!stateFor(fieldId).visible || stateFor(fieldId).readonly"
          @change="updateValue(fieldId, ($event.target as HTMLInputElement).checked)"
        />
        <span>{{ bundle.fields[fieldId].label }}</span>
      </label>

      <input
        v-else-if="bundle.fields[fieldId].type === 'date'"
        :id="fieldId"
        class="sas-control"
        type="date"
        :value="inputValue(fieldId)"
        :min="bundle.fields[fieldId].min ?? undefined"
        :max="bundle.fields[fieldId].max ?? undefined"
        :required="stateFor(fieldId).required"
        :readonly="stateFor(fieldId).readonly"
        :disabled="!stateFor(fieldId).visible"
        @input="updateValue(fieldId, ($event.target as HTMLInputElement).value || null)"
      />

      <input
        v-else-if="bundle.fields[fieldId].type === 'datetime'"
        :id="fieldId"
        class="sas-control"
        type="datetime-local"
        :value="inputValue(fieldId)"
        :min="bundle.fields[fieldId].min ?? undefined"
        :max="bundle.fields[fieldId].max ?? undefined"
        :required="stateFor(fieldId).required"
        :readonly="stateFor(fieldId).readonly"
        :disabled="!stateFor(fieldId).visible"
        @input="updateValue(fieldId, ($event.target as HTMLInputElement).value || null)"
      />

      <div v-else class="sas-field__unsupported">
        Тип поля «{{ bundle.fields[fieldId].type }}» пока не поддерживается renderer'ом.
      </div>

      <div
        v-if="bundle.fields[fieldId].provider && providerErrors[bundle.fields[fieldId].provider!]"
        class="sas-field__error"
      >
        {{ providerErrors[bundle.fields[fieldId].provider!] }}
      </div>
    </div>

    <div class="sas-form__actions">
      <button class="sas-button sas-button--primary" type="submit" :disabled="submitting || readingFiles">
        {{ readingFiles ? 'Читаем файлы…' : submitting ? 'Отправка…' : (submitLabel || 'Отправить') }}
      </button>
    </div>
    </fieldset>
  </form>
</template>

<style scoped>
.sas-form-fields { border: 0; padding: 0; margin: 0; min-width: 0; }
.sas-form { display: grid; gap: 18px; }
.sas-field { display: grid; gap: 7px; }
.sas-field__label { font-size: 14px; font-weight: 600; color: #242424; }
.sas-field__required { color: #b42318; }
.sas-field__description { color: #667085; font-size: 13px; }
.sas-control { width: 100%; box-sizing: border-box; min-height: 42px; padding: 9px 11px; border: 1px solid #b7c9d6; border-radius: 7px; background: #fff; color: #242424; font: inherit; }
.sas-control--textarea { resize: vertical; min-height: 96px; }
.sas-control:focus { outline: 2px solid rgba(31,78,121,.14); border-color: #1f4e79; }
.sas-checkbox { display: flex; align-items: center; gap: 9px; min-height: 36px; color: #242424; }
.sas-checkbox input { width: 18px; height: 18px; }
.sas-checkbox--disabled { opacity: .7; }
.sas-field__unsupported, .sas-field__error { padding: 10px; border-radius: 6px; font-size: 13px; }
.sas-field__unsupported { background: #f5f7f9; color: #667085; }
.sas-field__error { background: #fff4f2; color: #b42318; }
.sas-form__actions { display: flex; justify-content: flex-end; padding-top: 6px; }
.sas-button { min-height: 40px; border: 0; border-radius: 7px; padding: 0 16px; cursor: pointer; font-weight: 600; }
.sas-button--primary { background: #1f4e79; color: #fff; }
.sas-button:disabled { opacity: .65; cursor: default; }
</style>

<style scoped>
.sas-form-fields { border: 0; padding: 0; margin: 0; min-width: 0; }
.sas-choice-group{border:0;padding:0;margin:0;display:grid;gap:10px}.sas-search{margin-bottom:8px}
</style>
